/**
 * Lesson-specific vocabulary registry.
 *
 * Each entry is mapped to a specific moduleKey (or lessonId) and contains
 * lesson-aware copy — not a generic dictionary definition. Definitions
 * should explain how the term shows up in this specific lesson and why
 * it matters for the learner's decisions.
 *
 * Rules:
 *  - Only show a term inside a lesson if it lives in this registry for
 *    that lesson. Never fall back to a global glossary.
 *  - If `revealsAnswerForTriggers` matches the current question text,
 *    the term is suppressed for that question to avoid giving away the
 *    answer.
 *  - If a lesson has no curated terms, hide the panel entirely.
 */

export interface LessonVocabEntry {
  /** Stable id (kebab-case). */
  id: string;
  term: string;
  /** One sentence, plain definition. */
  shortDefinition: string;
  /** How the term appears in this specific lesson. */
  lessonSpecificDefinition: string;
  /** Why the learner should care. */
  whyItMatters: string;
  /** Concrete example grounded in this lesson. */
  example: string;
  /** Which lesson surfaces this term shows up on. */
  sourceTabs: Array<"video" | "insight" | "infographic" | "decision" | "quiz">;
  /** Question-text keywords that would let this term give away the answer. */
  revealsAnswerForTriggers?: string[];
}

export const LESSON_VOCABULARY_REGISTRY: Record<string, LessonVocabEntry[]> = {
  budget_builder: [
    {
      id: "take-home-pay",
      term: "Take-home pay",
      shortDefinition: "The money you actually receive after taxes and deductions.",
      lessonSpecificDefinition:
        "In Budget Builder, every percentage in the 50/30/20 plan is calculated from take-home pay — not the larger gross number on your offer letter.",
      whyItMatters:
        "Budgeting from gross income makes the plan look bigger than the money you can actually spend, which is the fastest way to fall behind.",
      example:
        "A $4,000 gross paycheck might land as $3,100 take-home. The budget runs on $3,100, not $4,000.",
      sourceTabs: ["video", "insight", "infographic", "quiz"],
    },
    {
      id: "needs-50",
      term: "Needs (the 50%)",
      shortDefinition: "Non-negotiable costs your life depends on.",
      lessonSpecificDefinition:
        "In the 50/30/20 plan, 50% of take-home pay covers needs like rent, utilities, groceries, transportation, and minimum debt payments.",
      whyItMatters:
        "If needs creep above 50%, wants and savings get squeezed and the plan stops working.",
      example: "Rent + utilities + groceries should fit inside the 50% slice.",
      sourceTabs: ["video", "insight", "infographic", "quiz"],
    },
    {
      id: "wants-30",
      term: "Wants (the 30%)",
      shortDefinition: "Flexible spending you could pause if you had to.",
      lessonSpecificDefinition:
        "30% covers the lifestyle layer — eating out, streaming, hobbies. In this lesson, wants are the dial you turn down when a surprise expense hits.",
      whyItMatters:
        "Wants are the safety valve. Trimming them temporarily is how you stay on plan without borrowing.",
      example:
        "After a $400 surprise bill, pausing takeout and one subscription absorbs the hit.",
      sourceTabs: ["video", "insight", "quiz"],
    },
    {
      id: "savings-20",
      term: "Savings (the 20%)",
      shortDefinition: "Money set aside for the future before you spend on wants.",
      lessonSpecificDefinition:
        "20% flows to savings and debt payoff above the minimums — including the emergency fund this lesson builds first.",
      whyItMatters:
        "Paying your future self before lifestyle spending is what makes the plan compound over time.",
      example: "$200 of every $1,000 take-home routes here automatically.",
      sourceTabs: ["video", "insight", "infographic", "quiz"],
    },
    {
      id: "emergency-fund",
      term: "Emergency fund",
      shortDefinition: "A savings buffer that protects your budget from surprise costs.",
      lessonSpecificDefinition:
        "In this lesson, the emergency fund is the first thing the 20% slice builds. It sits in cash so the budget keeps running when something unplanned hits.",
      whyItMatters:
        "Without it, one surprise cost forces borrowing, missed bills, or breaking the plan.",
      example: "Car repair, urgent dental visit, or replacing a broken phone.",
      sourceTabs: ["video", "insight", "infographic", "quiz"],
      // Hide this term while the question is literally asking what an emergency fund is for.
      revealsAnswerForTriggers: ["emergency fund", "purpose of an emergency"],
    },
    {
      id: "unexpected-expenses",
      term: "Unexpected expenses",
      shortDefinition: "Costs you did not plan for in this month's budget.",
      lessonSpecificDefinition:
        "Budget Builder treats unexpected expenses as inevitable — the plan is designed to absorb them through savings and flexible wants, not credit.",
      whyItMatters:
        "Pretending surprises will not happen is what breaks budgets. Planning for them is what keeps yours alive.",
      example: "A $400 car repair that was not on any line item this month.",
      sourceTabs: ["insight", "decision", "quiz"],
      revealsAnswerForTriggers: ["unexpected expense"],
    },
    {
      id: "consistency-over-perfection",
      term: "Consistency over perfection",
      shortDefinition: "Tracking regularly beats trying to be perfect once.",
      lessonSpecificDefinition:
        "This lesson closes on the idea that small, repeated check-ins compound into financial habits — a missed week does not break the budget, quitting does.",
      whyItMatters:
        "Perfectionism is the most common reason people abandon budgets in the first 60 days.",
      example: "A 10-minute weekly check beats one perfect spreadsheet you stop opening.",
      sourceTabs: ["video", "insight", "quiz"],
    },
  ],

  credit_management: [
    {
      id: "credit-utilization",
      term: "Credit utilization",
      shortDefinition: "How much of your credit limit you are currently using.",
      lessonSpecificDefinition:
        "In this module, utilization is balance ÷ limit. Keeping it low is one of the strongest levers on your simulated credit health.",
      whyItMatters:
        "High utilization signals overextension and drops your score even if you pay on time.",
      example: "$300 balance on a $1,000 limit = 30% utilization.",
      sourceTabs: ["video", "insight", "quiz"],
      revealsAnswerForTriggers: ["what is credit utilization", "utilization?"],
    },
    {
      id: "apr",
      term: "APR",
      shortDefinition: "The yearly interest rate charged when you carry a balance.",
      lessonSpecificDefinition:
        "APR is the price tag on borrowing in this lesson. Pay the statement in full and APR effectively does not touch you.",
      whyItMatters:
        "Carry a balance and APR quietly grows the debt month over month.",
      example: "A 24% APR on a $1,000 carried balance adds roughly $20 in interest each month.",
      sourceTabs: ["insight", "quiz"],
      revealsAnswerForTriggers: ["what does apr"],
    },
    {
      id: "minimum-payment",
      term: "Minimum payment",
      shortDefinition: "The smallest amount the card requires you to pay each month.",
      lessonSpecificDefinition:
        "Paying only the minimum keeps the account current but lets the balance and interest grow — this lesson treats minimum-only as a trap.",
      whyItMatters:
        "Most of a minimum payment goes to interest, so the balance barely moves.",
      example: "Paying $25 on a $1,000 balance at 24% APR can take years to clear.",
      sourceTabs: ["video", "insight", "quiz"],
    },
    {
      id: "payment-history",
      term: "Payment history",
      shortDefinition: "Your track record of paying on time.",
      lessonSpecificDefinition:
        "Payment history is the single biggest input to your simulated score in this module. One missed payment can outweigh months of good behavior.",
      whyItMatters:
        "Autopay exists mostly to protect this one factor.",
      example: "Missing a due date by even a few days can trigger fees and a score drop.",
      sourceTabs: ["video", "insight", "quiz"],
    },
    {
      id: "statement-balance",
      term: "Statement balance",
      shortDefinition: "What you owe at the end of a billing cycle.",
      lessonSpecificDefinition:
        "Paying the statement balance in full by the due date is how this lesson defines using a card without paying interest.",
      whyItMatters: "Pay-in-full turns a credit card into short-term, free borrowing.",
      example: "Statement says $420 — pay $420 by the due date and APR never kicks in.",
      sourceTabs: ["insight", "quiz"],
    },
    {
      id: "debt-avalanche",
      term: "Debt avalanche",
      shortDefinition: "Pay off the highest-interest debt first.",
      lessonSpecificDefinition:
        "Avalanche minimizes total interest paid — the math-optimal payoff order in this module.",
      whyItMatters: "Saves the most money over the life of the debt.",
      example: "Attack a 24% APR card before a 12% APR card, even if balances differ.",
      sourceTabs: ["insight", "quiz"],
      revealsAnswerForTriggers: ["avalanche method"],
    },
    {
      id: "debt-snowball",
      term: "Debt snowball",
      shortDefinition: "Pay off the smallest balance first for momentum.",
      lessonSpecificDefinition:
        "Snowball trades a little extra interest for behavioral wins — early payoffs that keep you in the game.",
      whyItMatters: "Momentum often beats math when motivation is the bottleneck.",
      example: "Clear a $200 balance fast, then roll that payment into the next debt.",
      sourceTabs: ["insight", "quiz"],
      revealsAnswerForTriggers: ["snowball method"],
    },
  ],

  scam_defense: [
    {
      id: "phishing",
      term: "Phishing",
      shortDefinition: "A fake message designed to trick you into sharing info or money.",
      lessonSpecificDefinition:
        "In Scam Defense, phishing shows up as urgent texts, emails, or DMs pretending to be a bank, delivery service, or boss.",
      whyItMatters:
        "One click on the wrong link can drain an account or hand over a login.",
      example: "A text claiming your bank locked your card with a link to 'verify' it.",
      sourceTabs: ["video", "insight", "quiz"],
    },
    {
      id: "urgency-pressure",
      term: "Urgency pressure",
      shortDefinition: "A scammer tactic that pushes you to act before you think.",
      lessonSpecificDefinition:
        "This module treats artificial urgency — 'act in 10 minutes or lose access' — as the single strongest scam signal.",
      whyItMatters:
        "Real institutions almost never demand instant action through a single channel.",
      example: "'Your account will be closed in 15 minutes unless you respond now.'",
      sourceTabs: ["video", "insight", "quiz"],
    },
    {
      id: "verify-by-callback",
      term: "Verify by callback",
      shortDefinition: "Hang up and call the institution directly using a trusted number.",
      lessonSpecificDefinition:
        "In this lesson, callback verification is the default response to any unexpected contact about money or access.",
      whyItMatters:
        "Scammers control whichever channel they reach you on — you take back control by switching channels.",
      example: "Use the number on the back of your card, not one provided in the message.",
      sourceTabs: ["insight", "decision", "quiz"],
    },
    {
      id: "gift-card-payment",
      term: "Gift-card payment request",
      shortDefinition: "An almost-always-a-scam demand to pay in gift cards.",
      lessonSpecificDefinition:
        "No legitimate bill, fine, or boss is paid in gift cards. In this module it is treated as a near-certain scam tell.",
      whyItMatters: "Gift-card payments are untraceable and unrecoverable.",
      example: "An 'IRS agent' demands Apple gift cards to settle a debt.",
      sourceTabs: ["video", "insight", "quiz"],
    },
  ],
};

/**
 * Look up curated vocabulary for a module/lesson key.
 * Returns an empty array (and the caller should hide the panel) when no
 * lesson-specific terms exist.
 */
export function getLessonVocabulary(key: string | undefined | null): LessonVocabEntry[] {
  if (!key) return [];
  return LESSON_VOCABULARY_REGISTRY[key] ?? [];
}

/**
 * Filter vocabulary for the active surface (e.g. quiz) and drop terms
 * that would directly reveal the answer to the current question.
 */
export function filterVocabForContext(
  terms: LessonVocabEntry[],
  opts: { surface: "video" | "insight" | "infographic" | "decision" | "quiz"; questionText?: string }
): LessonVocabEntry[] {
  const question = (opts.questionText ?? "").toLowerCase();
  return terms.filter((t) => {
    if (!t.sourceTabs.includes(opts.surface)) return false;
    if (opts.surface === "quiz" && t.revealsAnswerForTriggers?.length) {
      const reveals = t.revealsAnswerForTriggers.some((trigger) =>
        question.includes(trigger.toLowerCase())
      );
      if (reveals) return false;
    }
    return true;
  });
}